/**
 * The log window: the core's log, live. It loads the recent history when it opens, then adds
 * every `log.entry` event. Entries written by GUI windows (source `ui:...`) appear here too,
 * so a GUI problem and a core problem show up on the same timeline.
 */
import { For, Show, createMemo, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import type { Level } from "@protocol/Level";
import type { LogEntry } from "@protocol/LogEntry";
import { useApp } from "../../app/context";

/** How many entries the window keeps. */
const KEEP = 500;

/** Levels in order of severity. */
const LEVELS: Level[] = ["info", "warn", "error"];

/** The log window. */
export function LogWindow(): JSX.Element {
  const { client } = useApp();
  const [entries, setEntries] = createSignal<LogEntry[]>([]);
  const [minimum, setMinimum] = createSignal<Level>("info");
  const [source, setSource] = createSignal("");
  const [follow, setFollow] = createSignal(true);
  let list!: HTMLOListElement;

  /** Loads the latest entries (on every (re)connection). */
  const load = async () => {
    try {
      const response = await client.request({ method: "log.tail", params: { limit: 200 } });
      if (response.type === "log_entries") setEntries(response.entries);
    } catch {
      // Not connected yet: onOpen loads it.
    }
  };

  onMount(() => {
    void load();
    const offOpen = client.onOpen(() => void load());
    const offEvent = client.onEvent((event) => {
      if (event.event !== "log.entry") return;
      setEntries((now) => [...now.slice(-(KEEP - 1)), event.data.entry]);
      if (follow()) queueMicrotask(() => list && (list.scrollTop = list.scrollHeight));
    });
    onCleanup(() => {
      offOpen();
      offEvent();
    });
  });

  const shown = createMemo(() => {
    const min = LEVELS.indexOf(minimum());
    const text = source().toLowerCase();
    return entries().filter((e) => LEVELS.indexOf(e.level) >= min && (!text || e.source.toLowerCase().includes(text) || e.message.toLowerCase().includes(text)));
  });

  /** A timestamp as a local time of day. */
  const time = (ts: number) => new Date(ts).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  return (
    <div class="logwin">
      <div class="logbar">
        <div class="seg">
          <For each={LEVELS}>
            {(level) => (
              <button type="button" class="seg-b" classList={{ on: minimum() === level }} aria-pressed={minimum() === level} onClick={() => setMinimum(level)}>
                {level === "info" ? "All" : level === "warn" ? "Warn+" : "Errors"}
              </button>
            )}
          </For>
        </div>
        <input class="search" type="search" placeholder="Filter by source or text" onInput={(e) => setSource(e.currentTarget.value.trim())} />
        <label class="check">
          <input type="checkbox" checked={follow()} onChange={(e) => setFollow(e.currentTarget.checked)} /> Follow
        </label>
        <button type="button" class="mini" onClick={() => setEntries([])}>
          Clear
        </button>
      </div>
      <ol class="loglist" ref={list} data-log>
        <For each={shown()}>
          {(e) => (
            <li class={`lvl-${e.level}`}>
              <span class="lt">{time(e.ts)}</span>
              <span class="ll">{e.level}</span>
              <span class="ls">{e.source}</span>
              <span class="lm">
                {e.message}
                <For each={Object.entries(e.fields)}>
                  {([k, v]) => (
                    <span class="lf">
                      {" "}
                      {k}={v}
                    </span>
                  )}
                </For>
              </span>
            </li>
          )}
        </For>
      </ol>
      <Show when={!shown().length}>
        <p class="muted pad">No entry matches.</p>
      </Show>
    </div>
  );
}
